import { Routes, Route, useLocation, Navigate, Outlet } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from './context/AuthContext';
import { Suspense, lazy } from 'react';

// Declare Role inline to avoid bundler resolution issues
const Role = {
  ADMIN: 'ADMIN',
  DEVELOPER: 'DEVELOPER',
} as const;
type Role = (typeof Role)[keyof typeof Role];

// Layouts
import MainLayout from './components/layout/MainLayout';
import AdminLayout from './components/layout/AdminLayout';

// Loading component
import LoadingScreen from './components/ui/LoadingScreen';

// Lazy-loaded page components
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SnippetView = lazy(() => import('./pages/SnippetView'));
const SnippetEditor = lazy(() => import('./pages/SnippetEditor'));
const SnippetsList = lazy(() => import('./pages/SnippetsList'));
const Favourites = lazy(() => import('./pages/Favourites'));
const AdminSnippets = lazy(() => import('./pages/admin/AdminSnippets'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminStats = lazy(() => import('./pages/admin/AdminStats'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyPage = lazy(() => import('./pages/VerifyPage'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
// Shared snippet (public)
const SharedSnippet = lazy(() => import('./pages/SharedSnippet'));

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 10,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

// Protected route component
const ProtectedRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render the child routes if authenticated
  return <Outlet />;
};

// Role-based guard component
interface RoleGuardProps {
  role: Role;
  children: React.ReactNode;
}

const RoleGuard = ({ role, children }: RoleGuardProps) => {
  const { user } = useAuth();

  // Check if user has the required role
  if (user?.role !== role) {
    return <Navigate to="/" replace />;
  }

  // Render children if authorized
  return <>{children}</>;
};

// Main App component
function App() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public route */}
        <Route
          path="/login"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants}>
                <LoginPage />
              </motion.div>
            </Suspense>
          }
        />

        {/* Public shared-snippet route */}
        <Route
          path="/share/:token"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants}>
                <SharedSnippet />
              </motion.div>
            </Suspense>
          }
        />

        {/* Public register route */}
        <Route
          path="/register"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants}>
                <RegisterPage />
              </motion.div>
            </Suspense>
          }
        />

        {/* Forgot password */}
        <Route
          path="/auth/forgot"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants}>
                <ForgotPassword />
              </motion.div>
            </Suspense>
          }
        />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            {/* Dashboard */}
            <Route
              path="/"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <Dashboard />
                  </motion.div>
                </Suspense>
              }
            />

            {/* View snippet */}
            <Route
              path="/snippets/:id"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <SnippetView />
                  </motion.div>
                </Suspense>
              }
            />

            {/* All snippets (paginated list) */}
            <Route
              path="/snippets"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <SnippetsList />
                  </motion.div>
                </Suspense>
              }
            />

            {/* Favourites page */}
            <Route
              path="/favourites"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <Favourites />
                  </motion.div>
                </Suspense>
              }
            />

            {/* Create snippet */}
            <Route
              path="/editor"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <SnippetEditor mode="create" />
                  </motion.div>
                </Suspense>
              }
            />

            {/* Edit snippet */}
            <Route
              path="/editor/:id"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <SnippetEditor mode="edit" />
                  </motion.div>
                </Suspense>
              }
            />
          </Route>

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <RoleGuard role={Role.ADMIN}>
                <AdminLayout />
              </RoleGuard>
            }
          >
            {/* Admin snippets management */}
            <Route
              index
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <AdminSnippets />
                  </motion.div>
                </Suspense>
              }
            />

            {/* Admin users management */}
            <Route
              path="users"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <AdminUsers />
                  </motion.div>
                </Suspense>
              }
            />

            {/* Admin snippets (explicit path) */}
            <Route
              path="snippets"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <AdminSnippets />
                  </motion.div>
                </Suspense>
              }
            />

            {/* Admin settings (allowed email domains) */}
            <Route
              path="settings"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <AdminSettings />
                  </motion.div>
                </Suspense>
              }
            />

            {/* Admin statistics */}
            <Route
              path="stats"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <AdminStats />
                  </motion.div>
                </Suspense>
              }
            />
          </Route>
        </Route>

        {/* Not found page */}
        {/* Public email verification route */}
        <Route
          path="/auth/verify"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants}>
                <VerifyPage />
              </motion.div>
            </Suspense>
          }
        />

        {/* Reset password */}
        <Route
          path="/auth/reset"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants}>
                <ResetPassword />
              </motion.div>
            </Suspense>
          }
        />

        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants}>
                <NotFoundPage />
              </motion.div>
            </Suspense>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

export default App;
